import { useMemo, useState } from 'react';

import {
  Box,
  Container,
  Flex,
  HeadCellSortState,
  Headings,
  LoadingPage,
  OptionType,
  Pagination,
  Text,
  toggleSortStates,
  usePagination,
} from '@foradevelopment/fora-ui';
import { PayrollReportHistoryRowViewModel } from '@fora-monorepo/employer-my-pages-common/types';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';

import { useAppSelector } from '../../redux/hooks';
import {
  defaultReportHistoryListSortState,
  ReportHistorySortIdKeys,
  ReportHistorySortState,
} from '../components/ReportHistoryTable/ReportHistoryListSortState';
import { sortReportHistoryListIndices } from '../../common/utils/reportHistorySorting';
import { ReportHistoryTable } from '../components/ReportHistoryTable';
import { IntroText } from '../../components/IntroText/IntroText';
import { HistoryFilters } from '../../components';
import { useTypedTranslation } from '../../i18n/i18n';

const itemsPerPage = 25;

export const ReportHistoryView = () => {
  const { t } = useTypedTranslation();

  const employerInfo = useAppSelector(state => state.employer.employerInfo);
  const isLoadingReportHistory = useAppSelector(state => state.reportHistory.loadingReportHistory);

  const [selectedPartnerNumberOrganization] = useState<OptionType>({
    id: employerInfo.partnerNumber,
    name: employerInfo.organizationName,
  });

  const [tableSortState, setTableSortState] = useState<ReportHistorySortState>({
    ...defaultReportHistoryListSortState,
    date: HeadCellSortState.Desc,
  });
  const reportHistoryList: PayrollReportHistoryRowViewModel[] = useAppSelector(
    state => state.reportHistory.reportHistory
  );

  const sortedReportHistoryListIndices = useMemo(() => {
    const reportHistoryListIndices = reportHistoryList.map((_, ind) => ind);
    return sortReportHistoryListIndices({
      reportHistoryList: reportHistoryList,
      sortState: tableSortState,
      indices: reportHistoryListIndices,
    });
  }, [reportHistoryList, tableSortState]);

  const sortTable = (sortId: ReportHistorySortIdKeys) =>
    setTableSortState((state: { [x: string]: HeadCellSortState }) => {
      // Reset from initial state to make sure that only one column is sorted at a time
      const newState = { ...defaultReportHistoryListSortState };
      newState[sortId] = toggleSortStates(state[sortId]);
      return newState;
    });

  const { paginationProps, paginatedListIndices } = usePagination(sortedReportHistoryListIndices, itemsPerPage);

  return (
    <>
      <IntroText title={t('section.reportHistory.title')}>
        <Text size={'large'} marginBottom={10}>
          {t('section.reportHistory.description')}
        </Text>
      </IntroText>

      <HistoryFilters variant="report" />

      <Container>
        {isLoadingReportHistory === ThunkRequestStatus.Pending ? (
          <LoadingPage />
        ) : !reportHistoryList.length &&
          selectedPartnerNumberOrganization &&
          isLoadingReportHistory === ThunkRequestStatus.Fulfilled ? (
          <NoReportHistory />
        ) : isLoadingReportHistory === null ? (
          <NoSelectedOrganizationInfo />
        ) : isLoadingReportHistory === ThunkRequestStatus.Rejected ? (
          <ErrorWhenDataWasLoaded />
        ) : (
          <>
            <ReportHistoryTable
              reportHistory={reportHistoryList}
              reportHistoryListIndices={paginatedListIndices}
              sortState={tableSortState}
              sortTable={sortTable}
            />
            {!!reportHistoryList.length && <Pagination {...paginationProps} />}
          </>
        )}
      </Container>
    </>
  );
};

const NoReportHistory = () => {
  const { t } = useTypedTranslation();

  return (
    <Box backgroundColor="babyBlue" paddingBottom={40} paddingLeft={40} paddingRight={40} paddingTop={40}>
      <Flex justifyContent="center" rowGap={20} flexDirection="column">
        <Headings.H3>{t('infoMessages.filter.noEventsTitle')}</Headings.H3>
        <Text>{t('infoMessages.filter.noEventsText')}</Text>
      </Flex>
    </Box>
  );
};

const NoSelectedOrganizationInfo = () => {
  const { t } = useTypedTranslation();

  return (
    <Box backgroundColor="babyBlue" paddingBottom={40} paddingLeft={40} paddingRight={40} paddingTop={40}>
      <Flex justifyContent="center" rowGap={20} flexDirection="column">
        <Headings.H3>{t('infoMessages.filter.noCompanyTitle')}</Headings.H3>
        <Text>{t('infoMessages.filter.noCompanyText')} </Text>
      </Flex>
    </Box>
  );
};

const ErrorWhenDataWasLoaded = () => {
  const { t } = useTypedTranslation();
  return (
    <Box backgroundColor="babyPink" paddingBottom={40} paddingLeft={40} paddingRight={40} paddingTop={40}>
      <Flex justifyContent="center" rowGap={20} flexDirection="column">
        <Headings.H3>{t('errorMessages.filter.couldNotFetchData')}</Headings.H3>
        <Text>{t('errorMessages.filter.couldNotFetchDataText')} </Text>
      </Flex>
    </Box>
  );
};
